import React from "react";
import Permission from "../Permission";
import usePermissions from "../../hooks/usePermissions";
import { Wallet, Eye, EyeOff, AlertCircle } from "lucide-react";

const WalletBalancePage = () => {
  const { hasPermission, isSubUser, userRole } = usePermissions();

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-3xl font-semibold text-gray-800 flex items-center">
          <Wallet className="w-8 h-8 mr-3 text-blue-600" />
          Wallet Balances
        </h2>
        <p className="text-gray-600 mt-2">View your wallet balance information</p>

        {isSubUser && (
          <div className="mt-3 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-blue-500 mr-2" />
              <span className="text-blue-700 font-medium">
                Sub-User Account ({userRole?.replace('_', ' ').toUpperCase()})
              </span>
            </div>
          </div>
        )}
      </div>

      <Permission
        permission="balance-view"
        fallback={
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <EyeOff className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-red-800 mb-2">
              Access Restricted
            </h3>
            <p className="text-red-600 mb-4">
              You don't have permission to view balance information.
            </p>
            <div className="bg-red-100 rounded-lg p-4 text-left">
              <p className="text-sm text-red-700">
                <strong>Your Role:</strong> {userRole?.replace('_', ' ').toUpperCase()}
              </p>
              <p className="text-sm text-red-700 mt-1">
                <strong>Required Permission:</strong> balance-view
              </p>
              {(userRole === 'super_operation' || userRole === 'operation') && (
                <p className="text-sm text-red-700 mt-2">
                  💡 <em>{userRole.replace('_', ' ')} roles cannot view balance information by design.</em>
                </p>
              )}
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Main Balance */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Balance</p>
                  <p className="text-3xl font-bold">1,234.56</p>
                  <p className="text-blue-100 text-sm">USDT</p>
                </div>
                <Wallet className="w-12 h-12 text-blue-200" />
              </div>
            </div>

            {/* Available Balance */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Available Balance</p>
                  <p className="text-3xl font-bold">1,150.00</p>
                  <p className="text-green-100 text-sm">USDT</p>
                </div>
                <Eye className="w-12 h-12 text-green-200" />
              </div>
            </div>

            {/* Pending Balance */}
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm font-medium">Pending Balance</p>
                  <p className="text-3xl font-bold">84.56</p>
                  <p className="text-yellow-100 text-sm">USDT</p>
                </div>
                <AlertCircle className="w-12 h-12 text-yellow-200" />
              </div>
            </div>
          </div>

          {/* Transaction History Summary */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Recent Balance Changes
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Deposit Confirmed</span>
                <span className="text-green-600 font-semibold">+500.00 USDT</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Withdrawal Processed</span>
                <span className="text-red-600 font-semibold">-250.00 USDT</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-gray-600">Fee Applied</span>
                <span className="text-orange-600 font-semibold">-2.50 USDT</span>
              </div>
            </div>
          </div>

          {/* Wallet Management Actions */}
          <Permission action="manage_balance">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Balance Management
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
                  Transfer Funds
                </button>
                <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
                  Manage Wallet
                </button>
              </div>
            </div>
          </Permission>
        </div>
      </Permission>
    </div>
  );
};

export default WalletBalancePage;
